import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { usePlan } from '../store/planStore.js';
import {
  currentSession, isConfigured, signIn as apiSignIn, signOut as apiSignOut,
} from './client.js';
import { debounce, startSyncTriggers, syncWeek, type SyncStatus } from './engine.js';
import { adoptionNeeded, applyAdoption, markAdopted, type AdoptChoice, type AdoptQuestion } from './adopt.js';

export interface SharingContext {
  configured: boolean;
  signedIn: boolean;
  status: SyncStatus;
  question: AdoptQuestion | null;
  signIn(email: string, password: string): Promise<string | null>;
  signOut(): Promise<void>;
  syncNow(): void;
  answer(choice: AdoptChoice): Promise<void>;
}

const Ctx = createContext<SharingContext | null>(null);

export function useSharing(): SharingContext {
  const value = useContext(Ctx);
  if (!value) throw new Error('useSharing must be used inside a <SharingProvider>');
  return value;
}

/**
 * Owns everything about sharing, and sits ABOVE the screens on purpose.
 *
 * A screen unmounts on every tab change, so a question asked from inside one
 * would vanish the moment somebody tapped away — and the answer to "whose plan
 * wins" is not a thing that may be silently forgotten.
 */
export function SharingProvider({
  weekStart,
  children,
}: {
  weekStart: string;
  children: ReactNode;
}): React.JSX.Element {
  const { plan, settings, reload } = usePlan();
  const [signedIn, setSignedIn] = useState(false);
  const [status, setStatus] = useState<SyncStatus>({ state: 'off' });
  const [question, setQuestion] = useState<AdoptQuestion | null>(null);

  // The plan is read inside the debounced push, but must not RE-ARM it on every
  // keystroke — a ref keeps the latest value without making the effect churn.
  const weekRef = useRef(weekStart);
  weekRef.current = weekStart;

  useEffect(() => {
    void currentSession().then((session) => setSignedIn(session !== null));
  }, []);

  const run = useCallback(async (): Promise<void> => {
    if (!isConfigured()) return;
    if (!(await currentSession())) return;

    // Ask before merging. Adoption is the one case where an automatic merge
    // would be the wrong answer.
    const pending = await adoptionNeeded(weekRef.current);
    if (pending) {
      setQuestion(pending);
      return;
    }
    await markAdopted(weekRef.current);

    setStatus({ state: 'syncing' });
    const result = await syncWeek(weekRef.current);
    setStatus(result.status);
    if (result.plan ?? result.library ?? result.settings) reload();
  }, [reload]);

  const syncNow = useCallback((): void => {
    void run();
  }, [run]);

  // Sync on open, on foreground, and on reconnect.
  useEffect(() => {
    if (!signedIn) {
      setStatus({ state: 'off' });
      return;
    }
    void run();
    return startSyncTriggers(() => { void run(); });
  }, [signedIn, run, weekStart]);

  // Push on change, debounced. Ticking six things in a shop is six state
  // changes in as many seconds; without this it is six round trips on a
  // connection that may barely exist.
  const push = useMemo(() => debounce(() => { void run(); }, 1500), [run]);
  useEffect(() => {
    if (!signedIn) return;
    push();
    return () => push.cancel();
  }, [signedIn, push, plan, settings]);

  const value = useMemo<SharingContext>(
    () => ({
      configured: isConfigured(),
      signedIn,
      status,
      question,
      signIn: async (email, password) => {
        const { error } = await apiSignIn(email, password);
        if (!error) setSignedIn(true);
        return error;
      },
      signOut: async () => {
        await apiSignOut();
        setSignedIn(false);
        setStatus({ state: 'off' });
      },
      syncNow,
      answer: async (choice) => {
        await applyAdoption(weekRef.current, choice, settings.defaultStore);
        setQuestion(null);
        reload();
        void run();
      },
    }),
    [signedIn, status, question, syncNow, run, reload, settings.defaultStore],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}
